import type { Project, ProjectStatus } from '../../../types/projectsTable';
import type { CarrierPrice, Dimensions, Position, PositionDraft, TransportDimensions } from '../../../types/project';
import type {
  AnalogDimensions, AnalogSpec, MarketKey, MarketplacePrice, ProductAnalog, ProductAnalogs,
  ProductMarketplaces, ProductSupplierPortal, ResultPosition, SpecStatus, SpecSummary, SupplierOffer
} from '../../../types/results';
import type {
  AnalogCharacteristicDto, AnalogCharacteristicsSummaryDto, AnalogDto, ProductAnalogsDto, ProductDto,
  ProductMarketplacesDto, ProductSupplierPortalDto, ProjectDto, ReportDto, RestartProductBody, SearchProductRequestDto
} from './dto';
import type { ReportDelivery } from './types';

const MARKETS: { key: MarketKey; name: string; test: RegExp }[] = [
  { key: 'wildberries', name: 'Wildberries', test: /wildberries\.ru$/i },
];

const marketplacesOf = (offers: SupplierOffer[]): MarketplacePrice[] =>
  MARKETS.map(({ key, name, test }) => {
    const found = offers
      .filter((offer) => test.test(offer.host))
      .sort((a, b) => a.price - b.price)[0];

    return { key, name, price: found?.price ?? null, url: found?.url ?? '' };
  });

const hostOf = (url?: string): string => {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

export const mapResultPosition = (dto: ProductDto): ResultPosition => {
  const offers: SupplierOffer[] = (dto.sources ?? []).map((source) => ({
    id: source.id,
    name: source.title,
    host: hostOf(source.url),
    price: source.price ?? 0,
    url: source.url ?? '',
  }));

  return {
    id: dto.id,
    name: dto.name,
    spec: dto.additional_characteristic ?? '',
    qty: dto.amount ?? 1,
    unit: dto.unit || 'шт',
    logistics: (dto.avg_cost_of_delivery ?? 0) > 0,
    logisticsCost: dto.avg_cost_of_delivery ?? 0,
    offers,
    marketplaces: marketplacesOf(offers),
  };
};

const STATUS_MAP: Record<string, ProjectStatus> = {
  initial: 'new',
  created: 'new',

  in_process: 'run',
  processing: 'run',
  started: 'run',

  branches: 'run',
  optimized: 'run',
  screenshotting: 'run',
  delivery: 'run',

  finished: 'done',
  canceled: 'done',
  error: 'done',
  failed: 'done',
  stopped: 'done',
  all_stopped: 'done',
};

export const FALLBACK_MATCH_PCT = 50;

export const isFallbackMatch = (pct: number | null | undefined): boolean =>
  pct != null && pct < FALLBACK_MATCH_PCT;

const toStatus = (value?: string): ProjectStatus => {
  if (!value) return 'new';
  const mapped = STATUS_MAP[value.toLowerCase()];
  if (mapped) return mapped;

  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[mapProject] неизвестный report_status: "${value}" — показан как «В работе»`);
  }
  return 'run';
};

const toLocalDate = (raw?: string): string => {
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const mapProject = (dto: ProjectDto): Project => ({
  id: dto.id,
  name: dto.name,
  author: dto.full_name || dto.author_email || '—',
  date: toLocalDate(dto.created_at),
  price: dto.price ?? 0,
  logistics: dto.avg_cost_of_delivery ?? 0,
  status: toStatus(dto.report_status),
  isBranches: dto.report_status === 'branches',

  done: (dto.successed_product_count ?? 0) + (dto.canceled_product_count ?? 0),
  total: dto.product_count ?? 0,
});

const cheapestDelivery = (source: { delivery_prices?: { price: number | null }[] }): number => {
  const prices = (source.delivery_prices ?? [])
    .map((item) => item.price ?? 0)
    .filter((price) => price > 0);

  return prices.length ? Math.min(...prices) : 0;
};

const validPic = (url?: string): string => {
  if (!url) return '';
  try {
    const { hostname } = new URL(url);
    return hostname.startsWith('.') || !hostname.includes('.') ? '' : url;
  } catch {
    return '';
  }
};

const offersOf = (dto: ProductDto): SupplierOffer[] =>
  (dto.sources ?? []).map((source) => ({
    id: source.id,
    name: source.title,
    host: hostOf(source.url),
    price: source.price ?? 0,
    url: source.url ?? '',
    /* добавлен администратором вручную (POST /product/:id/sources) */
    isManual: Boolean((source as { is_manual?: boolean }).is_manual),
    pic: validPic((source as { pics?: string }).pics),
    // deliveryCost: cheapestDelivery(source as { delivery_prices?: { price: number | null }[] }),
    deliveryCost: dto.avg_cost_of_delivery ?? 0,
    dimensions: {
      weight: source.weight ?? null,
      length: source.length ?? null,
      width: source.width ?? null,
      height: source.height ?? null,
      isGross: Boolean(source.gross_weight),
      origin: source.dimensions_from,
      failed: Boolean(source.failed_to_calculate_dimensions),
    },
    /* parsing — расчёт доставки по источнику ещё идёт. */
    deliveryPending: source.status_delivery_source === 'parsing',
    deliveryPrices: (source.delivery_prices ?? [])
      .filter((item) => (item.price ?? 0) > 0)
      .map((item) => ({
        id: String(item.delivery_service_id),
        name: DELIVERY_SERVICES[String(item.delivery_service_id)] ??
          `Служба ${item.delivery_service_id}`,
        price: item.price as number,
        boxes: item.params?.number_of_boxes ?? null,
      })),
  }));

const dimensionsOf = (dto: ProductDto): Dimensions => {
  const own = dto.dimensions;
  const filled = Boolean(own && (own.length || own.width || own.height || own.weight));

  if (!filled) {
    return {
      weight: dto.weight ?? null,
      length: dto.length ?? null,
      width: dto.width ?? null,
      height: dto.height ?? null,
    };
  }

  return {
    weight: own?.weight ?? null,
    length: own?.length ?? null,
    width: own?.width ?? null,
    height: own?.height ?? null,
    origin: own?.source ?? undefined,
    confirmed: own?.confirmed,
    confidence: own?.confidence ?? null,
    reasoning: own?.reasoning ?? '',
  };
};

const transportOf = (dto: ProductDto): TransportDimensions => {
  const own = dto.transport_dimensions;
  const isEmpty = !own || !(own.length || own.width || own.height || own.weight);

  return {
    weight: own?.weight || null,
    length: own?.length || null,
    width: own?.width || null,
    height: own?.height || null,
    packagingClass: own?.packaging_class ?? null,
    matchesMain: isEmpty ? undefined : own?.matches_main,
    oversize: Boolean(own?.oversize),
    isManual: Boolean(own?.is_manual),
    isEmpty,
  };
};

export const mapProduct = (dto: ProductDto): Position => ({
  id: dto.id,
  name: dto.filtered_name || dto.name,
  spec: dto.additional_characteristic ?? '',
  qty: dto.amount ?? 1,
  unit: dto.unit || 'шт',
  region: dto.region_name ?? '',
  priceFrom: dto.price_min ?? 0,
  priceTo: dto.price_max ?? 0,
  requestPrice: dto.request_price ?? null,
  analogs: dto.search_analogs ?? true,
  logistics: Boolean((dto as { logistics?: boolean }).logistics),
  dimensions: dimensionsOf(dto),
  transport: transportOf(dto),
  badDimensions: dto.sources_with_incorrect_dimensions ?? 0,
  standardSizeId: dto.standard_size_id ?? null,
  carriers: carriersOf(dto),
  deliveryProcessing: dto.delivery_status === 'processing',

  offers: offersOf(dto),
  isDuplicate: Boolean(dto.is_duplicate),
  deliveryCost: dto.avg_cost_of_delivery ?? dto.min_cost_of_delivery ?? 0,
  deliveryWithoutPackaging: dto.avg_cost_of_delivery_without_packaging ?? 0,

  packagingCost: dto.cost_of_packaging ?? 0,
  pic: validPic(dto.pics),
  nameInvalid: dto.name_status === 'invalid',
  nameInvalidReason: dto.name_invalid_reason || 'Некорретный товар',
  analogsDelivery: dto.analogs_delivery ?? 'not_searched',

  // Поля для экрана активного поиска.
  searchStatus: dto.status ?? '',
  offersCount: (dto.sources ?? []).filter((source) => (source.price ?? 0) > 0).length,
  minFoundPrice:
    dto.min_price ??
    (dto.sources ?? [])
      .map((source) => source.price ?? 0)
      .filter((price) => price > 0)
      .sort((a, b) => a - b)[0] ??
    0,
  resultPrice: dto.price ?? 0,
  avgFoundPrice:
    dto.avg_price ??
    (() => {
      const prices = (dto.sources ?? [])
        .map((source) => source.price ?? 0)
        .filter((price) => price > 0);

      return prices.length
        ? prices.reduce((sum, price) => sum + price, 0) / prices.length
        : 0;
    })(),
});

export const positionToBody = (draft: PositionDraft): Record<string, unknown> => ({
  name: draft.name,
  amount: draft.qty || 1,
  unit: draft.unit || 'шт',
  request_price: draft.requestPrice ?? null,
});

export const positionToSearchProduct = (
  draft: PositionDraft | Position,
): SearchProductRequestDto => ({
  name: draft.name,
  additional_characteristic: draft.spec ?? '',
  amount: draft.qty || 1,
  unit: draft.unit || 'шт',
  region_name: draft.region ?? '',
  price_min: draft.priceFrom || null,
  price_max: draft.priceTo || null,
  request_price: draft.requestPrice ?? null,
  search_analogs: Boolean(draft.analogs),
  logistics: Boolean(draft.logistics),
});

export const editsToRestartBody = (edits: Partial<Position>): RestartProductBody => {
  const body: RestartProductBody = {};

  if (edits.name !== undefined) body.name = edits.name;
  if (edits.spec !== undefined) body.additional_characteristic = edits.spec;
  if (edits.region !== undefined) body.region_name = edits.region;
  if (edits.qty !== undefined) body.amount = edits.qty;
  if (edits.unit !== undefined) body.unit = edits.unit;
  if (edits.priceFrom !== undefined) body.price_min = edits.priceFrom || null;
  if (edits.priceTo !== undefined) body.price_max = edits.priceTo || null;
  if (edits.requestPrice !== undefined) body.request_price = edits.requestPrice ?? null;
  if (edits.analogs !== undefined) body.search_analogs = edits.analogs;
  if (edits.logistics !== undefined) body.logistics = edits.logistics;

  return body;
};

const MARKET_KEYS: Record<string, MarketKey> = {
  wildberries: 'wildberries',
  wb: 'wildberries',
};

const MARKET_NAMES: Record<MarketKey, string> = {
  wildberries: 'Wildberries',
};

export const mapProductMarketplaces = (dto: ProductMarketplacesDto): ProductMarketplaces => {
  const isSearching = dto.marketplaces_meta?.status === 'processing';

  const items = (dto.marketplaces ?? []).reduce<MarketplacePrice[]>((acc, item) => {
    const key = MARKET_KEYS[(item.marketplace ?? '').toLowerCase()];
    if (!key) return acc;

    acc.push({
      key,
      name: MARKET_NAMES[key],
      price: item.price ?? null,
      url: item.url ?? '',
      title: item.title ?? '',
      brand: item.brand ?? '',
      priceBefore: item.price_before_discount ?? null,
      rating: item.rating ?? null,
      inStock: item.in_stock ?? true,
      matchWhy: item.match_why ?? '',
    });
    return acc;
  }, []);

  const metaMarkets = (dto.marketplaces_meta as { marketplaces?: Record<string, { removed?: boolean }> } | undefined)
    ?.marketplaces;

  return {
    items,
    isSearching,
    matched: dto.marketplaces_meta?.matched ?? items.length,
    wildberriesRemoved: Boolean(metaMarkets?.wildberries?.removed),
  };
};

const PORTAL_REASON: Record<string, string> = {
  not_found: 'Портал ничего не вернул по этому наименованию',
  name_mismatch: 'Подходящий товар среди карточек не найден',
  no_price: 'У найденной карточки нет цены',
  no_url: 'У найденной карточки нет ссылки',
};

export const mapSupplierPortal = (dto: ProductSupplierPortalDto): ProductSupplierPortal => {
  const [found] = dto.supplier_portal ?? [];
  const meta = dto.supplier_portal_meta;

  const finished = meta?.status === 'done';

  const item =
    found && (found.price ?? 0) > 0
      ? {
        name: found.name ?? '',
        vendor: found.vendor ?? '',
        price: found.price ?? 0,
        url: found.url ?? '',
        matchWhy: found.match_why ?? meta?.match_why ?? '',
      }
      : null;

  return {
    item,
    isSearching: meta?.status === 'processing',
    emptyReason: PORTAL_REASON[meta?.reason ?? ''] ?? 'Товар не найден',
    removed: Boolean((meta as { removed?: boolean } | undefined)?.removed),
  };
};

export const DELIVERY_SERVICES: Record<string, string> = {
  '1': 'СДЭК',
  '2': 'Почта России',
  '3': 'DPD',
  '4': 'ПЭК',
  '5': 'ЖелДорЭкспедиция',
  '6': 'Деловые линии',
  '7': 'СберЛогистика',
};

export const CARRIER_IDS = ['1', '2', '3', '4', '5', '6', '7'];

export const DIMENSIONS_ORIGIN: Record<string, string> = {
  source: 'Парсинг данных из интернета',
  catalogue: 'Из справочника габаритов',
  default: 'Значения по умолчанию',

  catalog: 'Из справочника «Эталон»',
  consensus: 'Поставщики сошлись',
  ai: 'Определил ИИ-ассистент',
  parsing: 'Как нашлось при парсинге',

  user_data: 'Введены пользователем',

  not_calculated: 'Не рассчитывались',
};

export const PACKAGING_CLASS: Record<string, string> = {
  none: 'В заводской упаковке',
  soft: 'Мягкая доупаковка',
  crate: 'В обрешётке',
  pallet: 'На паллете',
  pallet_and_crate: 'На паллете, в обрешётке',
};

export const carriersOf = (dto: ProductDto): CarrierPrice[] => {
  const stats = new Map(
    (dto.delivery_stats ?? []).map((stat) => [String(stat.delivery_service_id), stat]),
  );

  return CARRIER_IDS.map((id) => {
    const stat = stats.get(id);
    const value = (raw: number | undefined) => (raw && raw > 0 ? raw : null);

    return {
      id,
      name: DELIVERY_SERVICES[id] ?? `Служба ${id}`,
      min: stat && stat.count > 0 ? value(stat.min_price) : null,
      avg: stat && stat.count > 0 ? value(stat.avg_price) : null,
      max: stat && stat.count > 0 ? value(stat.max_price) : null,
      outlier: stat?.outlier === 'high' || stat?.outlier === 'low' ? stat.outlier : null,
    };
  });
};

const ESTIMATE_STATUS = 'sum_of_avg_deliveries';

export const mapReportDelivery = (dto: ReportDto): ReportDelivery => {
  const dict = dto.dict_of_deliveries ?? {};

  const services = Object.entries(dict)
    .filter(([, price]) => Number(price) > 0)
    .map(([id, price]) => ({
      id,
      name: DELIVERY_SERVICES[id] ?? `Служба ${id}`,
      price: Number(price),
    }))
    .sort((a, b) => a.price - b.price);

  return {
    goods: dto.avg_price ?? 0,
    withoutOffers: Math.max(
      0,
      (dto.total_count ?? 0) - (dto.products_with_offers_count ?? dto.total_count ?? 0),
    ),
    isProcessing: dto.delivery_processing_status === 'processing',
    pendingProducts: dto.product_without_delivery_count ?? 0,
    total: dto.avg_cost_of_delivery ?? 0,
    minTotal: dto.min_cost_of_delivery ?? 0,
    services,
    isEstimate: dto.delivery_status === ESTIMATE_STATUS,
    weight: dto.weight ?? 0,
    volume: dto.volume ?? 0,
  };
};

/* ─── аналоги ─── */

const fullDims = (w?: number, l?: number, h?: number, wt?: number): AnalogDimensions | null =>
  w && l && h && wt ? { width: w, length: l, height: h, weight: wt } : null;

const ANALOG_AVAILABILITY = new Set(['in_stock', 'unknown', 'preorder']);

const CHARACTERISTIC_STATUS: Record<string, SpecStatus> = {
  match: 'ok',
  mismatch: 'diff',
  no_data: 'na',
};

const mapCharacteristic = (dto: AnalogCharacteristicDto): AnalogSpec => {
  const status = CHARACTERISTIC_STATUS[dto.status] ?? 'na';

  return {
    name: dto.name ?? '',
    base: dto.position_value ?? '',
    analog: dto.analog_value ?? '',
    analogUrl: status !== 'na' && dto.source_url ? dto.source_url : undefined,
    status,
  };
};

const mapCharacteristicsSummary = (dto?: AnalogCharacteristicsSummaryDto): SpecSummary => ({
  ok: dto?.match ?? 0,
  diff: dto?.mismatch ?? 0,
  na: dto?.no_data ?? 0,
});

const mapAnalog = (dto: AnalogDto): ProductAnalog => {
  const avg = dto.avg_cost_of_delivery ?? 0;

  const outliers = new Map(
    (dto.delivery_stats ?? []).map((stat) => [
      String(stat.delivery_service_id),
      stat.outlier === 'high' || stat.outlier === 'low' ? stat.outlier : null,
    ]),
  );

  return {
    title: dto.title ?? '',
    brand: dto.brand ?? '',
    model: dto.model ?? '',
    url: dto.url ?? '',
    host: dto.seller || hostOf(dto.url),
    price: dto.price ?? 0,
    availability: ANALOG_AVAILABILITY.has(dto.availability ?? '')
      ? (dto.availability as ProductAnalog['availability'])
      : 'unknown',
    pic: validPic(dto.image),
    match: dto.match === 'exact' ? 'exact' : 'variant',
    matchPct: typeof dto.match_pct === 'number' ? dto.match_pct : null,
    priceDeltaPct: dto.price_delta_pct ?? null,
    analogOf: dto.analog_of ?? '',
    flags: dto.flags ?? [],

    dimensions: fullDims(dto.width, dto.length, dto.height, dto.weight),
    transport: fullDims(
      dto.transport_width,
      dto.transport_length,
      dto.transport_height,
      dto.transport_weight,
    ),
    packagingClass: dto.packaging_class ?? null,
    transportMatchesMain: Boolean(dto.transport_matches_main),
    oversize: Boolean(dto.transport_oversize),
    dimensionsOrigin: dto.dimensions_origin ?? null,
    dimensionsConfirmed: Boolean(dto.dimensions_confirmed),
    dimensionsConfidence: dto.dimensions_confidence ?? null,
    dimensionsReasoning: dto.dimensions_reasoning ?? '',
    sidesLabelled: dto.dimensions_parsed?.sides_order !== 'assumed',
    shipping:
      dto.shipping_form && dto.shipping_form !== 'piece'
        ? {
          form: dto.shipping_form,
          places: dto.shipping_places ?? null,
          unitLengthM: dto.unit_length_m ?? null,
          estimated: Boolean(dto.shipping_estimated),
        }
        : null,

    delivery: avg > 0
      ? {
        min: dto.min_cost_of_delivery ?? avg,
        avg,
        max: dto.max_cost_of_delivery ?? avg,
        withoutPackaging: dto.avg_cost_of_delivery_without_packaging ?? avg,
        packaging: dto.cost_of_packaging ?? 0,
      }
      : null,
    deliveryPrices: (dto.delivery_prices ?? [])
      .filter((item) => (item.price ?? 0) > 0)
      .map((item) => ({
        outlier: outliers.get(String(item.delivery_service_id)) ?? null,
        id: String(item.delivery_service_id),
        name:
          DELIVERY_SERVICES[String(item.delivery_service_id)] ??
          `Служба ${item.delivery_service_id}`,
        price: item.price as number,
      })),
    deliveryInfo: dto.delivery_info,
    specs: (dto.characteristics ?? []).map(mapCharacteristic),
    specsSummary: mapCharacteristicsSummary(dto.characteristics_summary),
  };
};

/** processing — ищутся, done — закончен, skipped — не искали. Пустой статус = задача ещё не стартовала. */
const ANALOGS_FINISHED = new Set(['done', 'skipped']);

export const mapProductAnalogs = (dto: ProductAnalogsDto): ProductAnalogs => {
  const meta = dto.analogs_meta;
  const requested = dto.search_analogs ?? false;
  const status = (meta?.status ?? '').toLowerCase();
  const raw = meta?.logistics_status;
  const logistics: ProductAnalogs['logistics'] =
    raw === 'skipped' || raw === 'pending' || raw === 'processing' || raw === 'done'
      ? raw
      : 'pending';

  return {
    requested,
    isSearching: requested && !ANALOGS_FINISHED.has(status),
    skipped: status === 'skipped',
    items: (dto.analogs ?? []).map(mapAnalog),
    logistics,
    generated: meta?.generated ?? 0,
    verified: meta?.verified ?? 0,
    notes: meta?.notes ?? '',
  };
};

export const SHIPPING_FORM: Record<string, string> = {
  roll: 'рулон',
  coil: 'бухта',
  drum: 'барабан',
};